/**
 * TabelaExcel — tabela com cabeçalho no padrão do Excel: clicar no nome da
 * coluna ordena A→Z / Z→A, o funil abre o filtro por valores (buscar,
 * "(Selecionar todos)", Limpar) e a borda da coluna arrasta para ajustar a
 * largura (persistida por usuário, no servidor). Reusa os estilos do
 * PainelTabela e o ResizableColumns.
 *
 * Nasceu na aba "Solicitações" do Painel de Assistências e virou componente
 * quando a fila do comprador precisou da mesma coisa (ADR-0078) — uma
 * implementação só.
 *
 * Coluna: { key, label, valor(linha) -> texto p/ filtro/ordem/tooltip,
 *           render?(linha) -> nó rico, largura?, num?, filtro?: false }.
 * `selecao`: { selecionadas:Set, onToggle(id), onToggleTodas(ids, marcar) }.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiFilter, FiSearch } from "react-icons/fi";

import Checkbox from "../Checkbox";
import OpcaoDoFunil from "../PainelTabela/OpcaoDoFunil";
import {
  FunilBusca, FunilHead, FunilLimpar, FunilLista, FunilPopover, FunilVazio,
  Th, ThFunil, ThIconHint, ThInner,
} from "../PainelTabela/styles";
import {
  ColumnResizeProvider, ResetColumnsButton, ResizableColgroup, ResizableTh,
} from "../ResizableColumns";
import { VazioInline } from "../../pages/Suprimentos/_shared/Estados";

const LARGURA_SELECAO = 36;

const LARGURA_PADRAO = 170;
const LARGURA_MINIMA = 64;
// A alça de largura da última coluna passa 3px da borda; sem esta folga o
// card ganharia rolagem lateral só por causa dela.
const FOLGA_DA_ALCA = 4;

/**
 * Larguras iniciais que CABEM no card: as bases do catálogo são escaladas para
 * a largura disponível (zero scroll lateral por padrão, qualquer que seja o
 * conjunto de colunas escolhido). Só aparece rolagem — dentro do card — se a
 * própria pessoa alargar colunas além do espaço.
 */
export function largurasQueCabem(colunas, larguraDisponivel) {
  const bases = colunas.map((c) => c.largura || LARGURA_PADRAO);
  const larguras = bases.slice();
  if (larguraDisponivel > 0) {
    // Quem bate no mínimo sai da conta e o resto divide o que sobrou; repete
    // até ninguém mais bater (no máx. uma volta por coluna).
    const presas = new Set();
    for (let volta = 0; volta <= colunas.length; volta += 1) {
      const livre = larguraDisponivel - presas.size * LARGURA_MINIMA;
      const somaSoltas = bases.reduce((a, b, i) => (presas.has(i) ? a : a + b), 0) || 1;
      let prendeu = false;
      bases.forEach((b, i) => {
        if (presas.has(i)) { larguras[i] = LARGURA_MINIMA; return; }
        larguras[i] = Math.floor((b * livre) / somaSoltas);
        if (larguras[i] < LARGURA_MINIMA) { presas.add(i); prendeu = true; }
      });
      if (!prendeu) break;
    }
  }
  return colunas.map((c, i) => ({ key: c.key, defaultWidth: Math.max(LARGURA_MINIMA, larguras[i]) }));
}

function Funil({ coluna, opcoes, selecionadas, posicao, onChange, onFechar }) {
  const [busca, setBusca] = useState("");
  const q = busca.trim().toLowerCase();
  const visiveis = opcoes.filter((o) => o.label.toLowerCase().includes(q));
  const todos = opcoes.map((o) => o.value);

  const alternar = (valor) => {
    const set = new Set(selecionadas || todos);
    if (set.has(valor)) set.delete(valor); else set.add(valor);
    onChange(set.size === todos.length ? null : set);
  };

  return (
    <FunilPopover data-funil-popover style={{ top: posicao.top, left: posicao.left }}>
      <FunilHead>
        <span>{coluna.label}</span>
        <FunilLimpar type="button" onClick={() => { onChange(null); onFechar(); }}>Limpar</FunilLimpar>
      </FunilHead>
      <FunilBusca>
        <FiSearch size={13} aria-hidden="true" />
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar valor"
               aria-label={`Buscar valor de ${coluna.label}`} autoFocus />
      </FunilBusca>
      <FunilLista>
        <OpcaoDoFunil marcada={selecionadas === null} onAlternar={() => onChange(selecionadas === null ? new Set() : null)}>
          <span>(Selecionar todos)</span>
        </OpcaoDoFunil>
        {visiveis.map((o) => (
          <OpcaoDoFunil key={o.value} marcada={selecionadas === null || selecionadas.has(o.value)} onAlternar={() => alternar(o.value)}>
            <span>{o.label}</span>
            <span style={{ marginLeft: "auto", color: "var(--ink-4)", fontSize: 11 }}>{o.count}</span>
          </OpcaoDoFunil>
        ))}
        {visiveis.length === 0 && <FunilVazio>Nenhum valor</FunilVazio>}
      </FunilLista>
    </FunilPopover>
  );
}

export default function TabelaExcel({
  colunas, linhas, filtros, ordenacao, opcoesDe, onFiltro, onOrdenar, onLinha, vazio, tabelaRef,
  tableId, estiloCaixa, selecao = null, atributosDaLinha,
}) {
  const [funil, setFunil] = useState(null);
  const caixaRef = useRef(null);
  const [larguraCaixa, setLarguraCaixa] = useState(0);

  useEffect(() => {
    const el = caixaRef.current;
    if (!el) return undefined;
    // Durante a exportação em imagem o layout reflui (regras de export-mode);
    // remedir ali remontaria a tabela NO MEIO da captura e o html2canvas
    // perderia o elemento ("Unable to find element in cloned iframe").
    const medir = () => {
      if (document.body.getAttribute("data-export-mode")) return;
      setLarguraCaixa(Math.floor(el.getBoundingClientRect().width));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colunasDoProvider = useMemo(
    () => largurasQueCabem(colunas, larguraCaixa - FOLGA_DA_ALCA - (selecao ? LARGURA_SELECAO : 0)),
    [colunas, larguraCaixa, selecao],
  );

  useEffect(() => {
    if (!funil) return undefined;
    const fora = (e) => {
      if (!e.target.closest("[data-funil-popover]") && !e.target.closest("[data-funil-btn]")) setFunil(null);
    };
    const esc = (e) => e.key === "Escape" && setFunil(null);
    const rolou = () => setFunil(null);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", rolou, true);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", rolou, true);
    };
  }, [funil]);

  const abrirFunil = (key, e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const left = Math.min(r.left, window.innerWidth - 262);
    setFunil((atual) => (atual?.key === key ? null : { key, top: r.bottom + 6, left }));
  };

  const colunaDoFunil = funil && colunas.find((c) => c.key === funil.key);

  return (
    // O elemento capturado na exportação é ESTA caixa (estável): o que está
    // dentro do Provider remonta quando colunas/largura mudam.
    <div
      ref={(el) => { caixaRef.current = el; if (tabelaRef) tabelaRef.current = el; }}
      style={estiloCaixa}
    >
    {larguraCaixa > 0 && (
    <ColumnResizeProvider
      // Conjunto de colunas ou largura do card mudou → larguras-padrão novas.
      key={`${colunas.map((c) => c.key).join("|")}:${larguraCaixa}`}
      tableId={tableId}
      persistirNoBackend
      columns={colunasDoProvider}
    >
      <div data-export-no-print style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 4px" }}>
        <ResetColumnsButton />
      </div>
      <div style={{ overflowX: "auto", paddingRight: FOLGA_DA_ALCA }}>
        <table className="dtable" style={{ tableLayout: "fixed", width: "100%" }}>
          {selecao ? (
            // Coluna da caixa de seleção: largura fixa, fora do redimensionamento.
            <colgroup><col style={{ width: LARGURA_SELECAO }} /></colgroup>
          ) : null}
          <ResizableColgroup />
          <thead>
            <tr>
              {selecao ? (
                <Th style={{ position: "relative", top: 0, width: LARGURA_SELECAO }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    aria-label="Selecionar todas as linhas visíveis"
                    checked={linhas.length > 0 && linhas.every((l) => selecao.selecionadas.has(l.id))}
                    onChange={() => selecao.onToggleTodas(
                      linhas.map((l) => l.id),
                      !linhas.every((l) => selecao.selecionadas.has(l.id)),
                    )}
                  />
                </Th>
              ) : null}
              {colunas.map((c) => {
                const ordenada = ordenacao?.key === c.key;
                return (
                  <ResizableTh as={Th} key={c.key} columnKey={c.key} onClick={() => onOrdenar(c.key)}
                               title={`Ordenar por ${c.label}`}
                               // O Th do PainelTabela gruda no topo da PÁGINA; aqui o
                               // card é o contêiner e o deslocamento cobriria as linhas. Fica
                               // `relative` (não static): a alça de largura é absolute.
                               style={{ cursor: "pointer", position: "relative", top: 0 }}
                               aria-sort={ordenada ? (ordenacao.dir === "asc" ? "ascending" : "descending") : "none"}>
                    {/* Rótulo maior que a coluna encolhe com reticências em vez
                        de vazar e criar rolagem lateral no card. */}
                    <ThInner style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                      {ordenada ? (
                        ordenacao.dir === "asc"
                          ? <FiChevronUp size={13} data-export-no-print />
                          : <FiChevronDown size={13} data-export-no-print />
                      ) : (
                        <ThIconHint data-export-no-print><FiChevronDown size={13} /></ThIconHint>
                      )}
                      {c.filtro !== false && (
                        <ThFunil type="button" data-funil-btn data-export-no-print
                                 $ativo={Boolean(filtros[c.key])}
                                 aria-label={`Filtrar ${c.label}`} title={`Filtrar ${c.label}`}
                                 onClick={(e) => abrirFunil(c.key, e)}>
                          <FiFilter size={12} />
                        </ThFunil>
                      )}
                    </ThInner>
                  </ResizableTh>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id} className="rowlink" tabIndex={0}
                  {...(atributosDaLinha ? atributosDaLinha(l) : {})}
                  onClick={() => onLinha(l)} onKeyDown={(e) => e.key === "Enter" && onLinha(l)}>
                {selecao ? (
                  <td onClick={(e) => { e.stopPropagation(); selecao.onToggle(l.id); }}>
                    <Checkbox checked={selecao.selecionadas.has(l.id)} readOnly aria-label={`Selecionar ${l.rotuloSelecao || l.numero || l.id}`} />
                  </td>
                ) : null}
                {colunas.map((c) => {
                  const v = c.valor(l);
                  const texto = v === null || v === undefined || v === "" ? "—" : String(v);
                  return (
                    <td key={c.key} className={c.num ? "num" : "ell"} title={texto}
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.render ? c.render(l) : texto}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {linhas.length === 0 && <div style={{ padding: 18 }}><VazioInline>{vazio}</VazioInline></div>}
      </div>

      {colunaDoFunil && (
        <Funil
          key={colunaDoFunil.key}
          coluna={colunaDoFunil}
          opcoes={opcoesDe(colunaDoFunil)}
          selecionadas={filtros[colunaDoFunil.key] || null}
          posicao={funil}
          onChange={(set) => onFiltro(colunaDoFunil.key, set)}
          onFechar={() => setFunil(null)}
        />
      )}
    </ColumnResizeProvider>
    )}
    </div>
  );
}
